import { Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { QueueProvider } from "@/contexts/QueueContext";
import { AudioPlayerProvider } from "@/contexts/AudioPlayerContext";
import { PostHogProvider } from "@/components/PostHogProvider";
import { SessionTracker } from "@/components/SessionTracker";
import { InstallBanner } from "@/components/InstallBanner";
import { ScrollToTop } from "@/components/ScrollToTop";
import { IOSPWAAudioHint } from "@/components/IOSPWAAudioHint";
import { clearExpiredCache } from "@/lib/audioCache";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SessionExpiredModal } from "@/components/SessionExpiredModal";
import { RequireAuth, RequireAdmin } from "@/components/RequireAuth";
import { CrispChat } from "@/components/CrispChat";
import { NoIndex } from "@/components/NoIndex";

/** Wrap a public-but-hidden page (auth pages) so search engines skip it. */
const Hidden = ({ children }: { children: React.ReactNode }) => (
  <><NoIndex />{children}</>
);

const DEFAULT_DESCRIPTION = "Turn any podcast or YouTube video into a short AI-narrated summary you can read or listen to in minutes. Save hours every month without missing what matters.";
// Keep only the landing page synchronous for fastest initial load
import Index from "./pages/Index";
// Eager-load ResetPassword so users arriving from the iOS app never see a Suspense flash
import ResetPassword from "./pages/ResetPassword";

// Lazy load auth pages - not needed for landing page visitors
const Login = lazyWithRetry(() => import("./pages/Login"));
const SignUp = lazyWithRetry(() => import("./pages/SignUp"));

// Lazy load the audio player - only needed when user plays audio
const GlobalAudioPlayer = lazyWithRetry(() => import("./components/audio/GlobalAudioPlayer").then(m => ({ default: m.GlobalAudioPlayer })));

// Lazy load everything else for better initial bundle size

const Library = lazyWithRetry(() => import("./pages/Library"));
const Brief = lazyWithRetry(() => import("./pages/Brief"));
const Search = lazyWithRetry(() => import("./pages/Search"));
const SearchCategory = lazyWithRetry(() => import("./pages/SearchCategory"));
const Shows = lazyWithRetry(() => import("./pages/Shows"));
const ShowsDirectory = lazyWithRetry(() => import("./pages/ShowsDirectory"));
const ShowsExplore = lazyWithRetry(() => import("./pages/ShowsExplore"));
const PublicShowDetail = lazyWithRetry(() => import("./pages/PublicShowDetail"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Install = lazyWithRetry(() => import("./pages/Install"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const EULA = lazyWithRetry(() => import("./pages/EULA"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const ShowDiscovery = lazyWithRetry(() => import("./pages/ShowDiscovery"));
const ShowListing = lazyWithRetry(() => import("./pages/ShowListing"));
const EpisodeDetail = lazyWithRetry(() => import("./pages/EpisodeDetail"));
const Analytics = lazyWithRetry(() => import("./pages/Analytics"));
const ChannelVideos = lazyWithRetry(() => import("./pages/ChannelVideos"));
const VideoDetail = lazyWithRetry(() => import("./pages/VideoDetail"));

// Admin pages (lowest priority - few users access these)
const AdminLayout = lazyWithRetry(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminUsers = lazyWithRetry(() => import("./pages/AdminUsers"));
const AdminAnalytics = lazyWithRetry(() => import("./pages/AdminAnalytics"));
const AdminPerformance = lazyWithRetry(() => import("./pages/AdminPerformance"));
const AdminCosts = lazyWithRetry(() => import("./pages/AdminCosts"));
const AdminErrors = lazyWithRetry(() => import("./pages/AdminErrors"));
const AdminAIConfig = lazyWithRetry(() => import("./pages/AdminAIConfig"));
const AdminBriefs = lazyWithRetry(() => import("./pages/AdminBriefs"));
const AdminReferrals = lazyWithRetry(() => import("./pages/AdminReferrals"));
const AdminOnboarding = lazyWithRetry(() => import("./pages/AdminOnboarding"));
const OAuthConsent = lazyWithRetry(() => import("./pages/OAuthConsent"));



// Clear expired audio cache on app startup
clearExpiredCache();

const queryClient = new QueryClient();

// Page loading fallback
const PageLoader = () => (
  <div className="min-h-screen gradient-dark flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
  </div>
);
// Legacy /shows/:slug → /explore/:slug (preserves query string + SEO equity)
function LegacyShowsSlugRedirect() {
  const { slug } = useParams();
  const location = useLocation();
  return <Navigate to={`/explore/${slug}${location.search}`} replace />;
}
function LegacyShowsSubpathRedirect({ subpath }: { subpath: 'episodes' | 'briefs' }) {
  const { slug } = useParams();
  const location = useLocation();
  return <Navigate to={`/explore/${slug}/${subpath}${location.search}`} replace />;
}

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <Helmet>
        <meta name="description" content={DEFAULT_DESCRIPTION} />
        <meta property="og:description" content={DEFAULT_DESCRIPTION} />
        <meta name="twitter:description" content={DEFAULT_DESCRIPTION} />
      </Helmet>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <SessionExpiredModal />
                <CrispChat />
            <QueueProvider>
              <AudioPlayerProvider>
                <SessionTracker />
                <Toaster />
                <IOSPWAAudioHint />
              <BrowserRouter>
                <ScrollToTop />
                <PostHogProvider>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/login" element={<Hidden><Login /></Hidden>} />
                      <Route path="/signup" element={<Hidden><SignUp /></Hidden>} />
                      <Route path="/reset-password" element={<Hidden><ResetPassword /></Hidden>} />
                      <Route path="/.lovable/oauth/consent" element={<Hidden><OAuthConsent /></Hidden>} />
                      <Route path="/library" element={<RequireAuth><Library /></RequireAuth>} />
                      <Route path="/brief/:slug" element={<Brief />} />
                      <Route path="/search" element={<RequireAuth><Search /></RequireAuth>} />
                      <Route path="/search/category/:genreSlug" element={<RequireAuth><SearchCategory /></RequireAuth>} />
                      {/* Public SEO directory under /explore */}
                      <Route path="/explore" element={<ShowsDirectory />} />
                      <Route path="/explore/podcasts" element={<ShowsExplore source="podcast" />} />
                      <Route path="/explore/youtube" element={<ShowsExplore source="youtube" />} />
                      <Route path="/explore/:slug" element={<PublicShowDetail />} />
                      <Route path="/explore/:slug/episodes" element={<ShowListing variant="episodes" />} />
                      <Route path="/explore/:slug/briefs" element={<ShowListing variant="briefs" />} />
                      {/* Authenticated app */}
                      <Route path="/feed" element={<RequireAuth><Shows /></RequireAuth>} />
                      <Route path="/podcasts" element={<Navigate to="/feed" replace />} />
                      <Route path="/show/:slug" element={<ShowDiscovery />} />
                      <Route path="/episode/:slug" element={<EpisodeDetail />} />
                      {/* Legacy /shows/... redirects → /explore/... (preserve indexed-URL traffic) */}
                      <Route path="/shows" element={<Navigate to="/explore" replace />} />
                      <Route path="/shows/podcasts" element={<Navigate to="/explore/podcasts" replace />} />
                      <Route path="/shows/youtube" element={<Navigate to="/explore/youtube" replace />} />
                      <Route path="/shows/:slug" element={<LegacyShowsSlugRedirect />} />
                      <Route path="/shows/:slug/episodes" element={<LegacyShowsSubpathRedirect subpath="episodes" />} />
                      <Route path="/shows/:slug/briefs" element={<LegacyShowsSubpathRedirect subpath="briefs" />} />
                      <Route path="/analytics" element={<RequireAuth><Analytics /></RequireAuth>} />
                      <Route path="/channel/:channelId" element={<RequireAuth><ChannelVideos /></RequireAuth>} />
                      <Route path="/video/:slug" element={<RequireAuth><VideoDetail /></RequireAuth>} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
                      <Route path="/install" element={<Install />} />
                      <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
                        <Route index element={<Navigate to="/admin/users" replace />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="analytics" element={<AdminAnalytics />} />
                        
                        <Route path="performance" element={<AdminPerformance />} />
                        <Route path="costs" element={<AdminCosts />} />
                        <Route path="errors" element={<AdminErrors />} />
                        <Route path="config" element={<AdminAIConfig />} />
                        <Route path="briefs" element={<AdminBriefs />} />
                        <Route path="referrals" element={<AdminReferrals />} />
                        <Route path="onboarding" element={<AdminOnboarding />} />
                      </Route>

                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/eula" element={<EULA />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                  <Suspense fallback={null}>
                    <GlobalAudioPlayer />
                  </Suspense>
                  <InstallBanner />
                </PostHogProvider>
              </BrowserRouter>
              </AudioPlayerProvider>
            </QueueProvider>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
